import React, { useState, useEffect } from 'react';
import { Table, Card, Input, Button } from 'react-daisyui';
import { Search, ChevronLeft, ChevronRight } from 'react-feather';
import TopNavbar from './TopNavbar';
import Sidebar from './Sidebar';

const ITEMS_PER_PAGE = 50; // Standard items per page

const TokensPage = () => {
  const [tokens, setTokens] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredTokens, setFilteredTokens] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [nextPageParams, setNextPageParams] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    fetchTokens();
  }, [currentPage]);

  useEffect(() => {
    if (Array.isArray(tokens)) {
      const filtered = tokens.filter(token => 
        (token.name && token.name.toLowerCase().includes(searchQuery.toLowerCase())) || 
        (token.symbol && token.symbol.toLowerCase().includes(searchQuery.toLowerCase()))
      );
      setFilteredTokens(filtered);
    }
  }, [searchQuery, tokens]);

  const fetchTokens = async (pageParams = null) => {
    setIsLoading(true);
    setError(null);
    try {
      let url = 'https://berachain-v2-blacknodes-el-api.blacknodes.net/api/v2/tokens';
      if (pageParams) {
        const params = new URLSearchParams(pageParams);
        url += `?${params.toString()}`;
      }

      const response = await fetch(url);
      const data = await response.json();
      
      if (data && data.items && Array.isArray(data.items)) {
        setTokens(data.items);
        setFilteredTokens(data.items);
        setNextPageParams(data.next_page_params);
      } else {
        throw new Error('Unexpected data format');
      }
    } catch (error) {
      console.error('Error fetching tokens:', error);
      setError('Failed to fetch tokens. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleNextPage = () => {
    if (nextPageParams) {
      setCurrentPage(prev => prev + 1);
      fetchTokens(nextPageParams);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
      fetchTokens(); // This will fetch the first page again
    }
  };

  const getTokenNumber = (index) => {
    return ((currentPage - 1) * ITEMS_PER_PAGE) + index + 1;
  };

  const formatSupply = (supply, decimals) => {
    if (!supply) return 'N/A';
    if (!decimals) return supply;
    
    const divisor = Math.pow(10, parseInt(decimals));
    const formattedSupply = (parseFloat(supply) / divisor).toLocaleString(undefined, {
      maximumFractionDigits: 2
    });
    
    return formattedSupply;
  };

  const truncateAddress = (address) => {
    if (typeof address === 'string' && address.length > 10) {
      return `${address.slice(0, 6)}...${address.slice(-4)}`;
    }
    return address || 'N/A';
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-black text-white flex">
        <Sidebar />
        <div className="flex-1 p-4 pl-20">
          <TopNavbar />
          <div className="text-white text-center mt-8">Loading tokens...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-black text-white flex">
        <Sidebar />
        <div className="flex-1 p-4 pl-20">
          <TopNavbar />
          <div className="text-red-500 text-center mt-8">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white flex">
      <Sidebar />
      <div className="flex-1 p-4 pl-20">
        <TopNavbar />
        <div className="mb-4">
          <Card className="bg-gray-900 shadow-[0px_20px_207px_10px_rgba(38,_126,_218,_0.93)]">
            <Card.Body>
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-bold text-white">Tokens</h2>
                <div className="relative">
                  <Input 
                    type="text"
                    placeholder="Search tokens"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-64 pl-10 pr-4 py-2 rounded-lg bg-gray-800 text-white border border-gray-700"
                  />
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                </div>
              </div>
              {filteredTokens.length > 0 ? (
                <>
                  <Table className="w-full">
                    <Table.Head>
                      <span>#</span>
                      <span>Token</span>
                      <span>Address</span>
                      <span>Holders</span>
                      <span>Total Supply</span>
                    </Table.Head>
                    <Table.Body>
                      {filteredTokens.map((token, index) => (
                        <Table.Row key={token.address || index} className="hover:bg-gray-800">
                          <span className="text-gray-400">{getTokenNumber(index)}</span>
                          <span>
                            <a href={`/token/${token.address}`} className="text-cyan-400 hover:underline">
                              {token.name || 'N/A'} ({token.symbol || 'N/A'})
                            </a>
                          </span>
                          <span>
                            <a href={`/address/${token.address}`} className="text-cyan-400 hover:underline">
                              {truncateAddress(token.address)}
                            </a>
                          </span>
                          <span>{token.holders?.toLocaleString() || 'N/A'}</span>
                          <span>{`${formatSupply(token.total_supply, token.decimals)} ${token.symbol || ''}`}</span>
                        </Table.Row>
                      ))}
                    </Table.Body>
                  </Table>
                  <div className="flex justify-between items-center mt-4">
                    <span className="text-gray-400">
                      Page {currentPage}
                    </span>
                    <div className="flex gap-2">
                      <Button 
                        color="ghost" 
                        onClick={handlePrevPage}
                        disabled={currentPage === 1}
                        className="hover:bg-gray-800"
                      >
                        <ChevronLeft size={20} />
                      </Button>
                      <Button 
                        color="ghost" 
                        onClick={handleNextPage}
                        disabled={!nextPageParams}
                        className="hover:bg-gray-800"
                      >
                        <ChevronRight size={20} />
                      </Button>
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-white text-center mt-4">No tokens found</div>
              )}
            </Card.Body>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default TokensPage;